import type { DockviewApi } from 'dockview';

import { useWorkspaceStore } from './store';
import { getDockApi, setTabStripEl, setWorkspaceEl } from './refs';
import { snapRect } from './snap';
import { loadLayout, saveLayout, type PersistedLayout } from './persistence';
import {
  FLOATING_Z_BASE,
  FLOATING_Z_CAP,
  PANEL_TYPES,
  SNAP_MODE,
  isFloatingLike,
  isSnapped,
  type PanelMode,
  type PanelType,
  type Rect,
  type SnapRegion,
  type WorkspacePanelState,
} from './types';

const store = useWorkspaceStore;

const MIN_W = 240;
const MIN_H = 150;

const DEFAULT_SIZE: Record<PanelType, { width: number; height: number }> = {
  home: { width: 820, height: 580 },
  files: { width: 880, height: 560 },
  doc: { width: 620, height: 460 },
  markdown: { width: 620, height: 480 },
  graph: { width: 820, height: 580 },
  orbit: { width: 820, height: 580 },
  ask: { width: 420, height: 560 },
};

const DOCK_COMPONENT = 'workspacePanel';

let idCounter = 0;
let ready = false;
let draggingId: string | null = null;
let saveTimer: ReturnType<typeof setTimeout> | null = null;

/** A document/note the explorer can open into a panel. */
export interface DocDescriptor {
  /** Node/document id (also the panel identity). */
  id: string;
  title: string;
  /** How PanelContent should fetch content. */
  docKind: 'document' | 'node';
  nodeType?: string | null;
}

// ── helpers ──────────────────────────────────────────────────────────────
const get = () => store.getState();
const panel = (id: string): WorkspacePanelState | undefined => get().panels[id];

function requirePanel(id: string): WorkspacePanelState {
  const p = panel(id);
  if (!p) throw new Error(`unknown panel: "${id}"`);
  return p;
}

function patch(id: string, changes: Partial<WorkspacePanelState>): void {
  store.setState((s) => {
    const cur = s.panels[id];
    if (!cur) return s;
    return { panels: { ...s.panels, [id]: { ...cur, ...changes } } };
  });
}

/**
 * Renumber every floating/snapped panel's zIndex back into a tight band
 * starting at FLOATING_Z_BASE (relative stacking order preserved), and
 * return the new top value. See FLOATING_Z_CAP's doc comment (types.ts) for
 * why this exists: zTop is persisted and only ever grows across a
 * long-lived session, so without periodic compaction it could eventually
 * drift into the fixed drop-preview/maximized tiers reserved above it.
 */
function compactZOrder(): number {
  const floating = Object.values(get().panels)
    .filter((p) => isFloatingLike(p.mode))
    .sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
  if (floating.length === 0) return FLOATING_Z_BASE - 1;
  store.setState((s) => {
    const panels = { ...s.panels };
    let z = FLOATING_Z_BASE;
    for (const p of floating) panels[p.id] = { ...p, zIndex: z++ };
    return { panels };
  });
  return FLOATING_Z_BASE + floating.length - 1;
}

function nextZ(): number {
  let top = get().zTop;
  if (top + 1 > FLOATING_Z_CAP) top = compactZOrder();
  const z = top + 1;
  store.setState({ zTop: z });
  return z;
}

function floatingCount(): number {
  return Object.values(get().panels).filter((p) => isFloatingLike(p.mode)).length;
}

function clamp(x: number, y: number, w: number, h: number) {
  const ws = get().workspaceRect;
  const bw = ws.width || w;
  const bh = ws.height || h;
  const width = Math.max(MIN_W, Math.min(w, bw));
  const height = Math.max(MIN_H, Math.min(h, bh));
  const nx = Math.max(0, Math.min(x, Math.max(0, bw - width)));
  const ny = Math.max(0, Math.min(y, Math.max(0, bh - height)));
  return { x: Math.round(nx), y: Math.round(ny), width: Math.round(width), height: Math.round(height) };
}

function defaultFloatGeom(type: PanelType) {
  const size = DEFAULT_SIZE[type];
  const n = floatingCount();
  return clamp(48 + n * 28, 40 + n * 28, size.width, size.height);
}

/** A clean snapshot (no nested previousState) for restore points. */
function snapshot(p: WorkspacePanelState): WorkspacePanelState {
  return { ...p, previousState: null };
}

// ── Dockview sync ─────────────────────────────────────────────────────────
function addToDock(id: string): void {
  const api = getDockApi();
  const p = panel(id);
  if (!api || !p || api.getPanel(id)) return;
  api.addPanel({ id, component: DOCK_COMPONENT, title: p.title, params: { panelId: id } });
}

function removeFromDock(id: string): void {
  const api = getDockApi();
  const dp = api?.getPanel(id);
  if (api && dp) api.removePanel(dp);
}

/** Hand the dock's front tab to whatever is left, when `id` stops being a tab
 * (it floated, snapped, or maximized). Without this the dock would keep
 * pointing at a panel that is no longer in it. */
function releaseTab(id: string): void {
  store.setState((s) => {
    if (s.activeTabId !== id) return s;
    const remaining = s.tabOrder.filter((x) => x !== id);
    return { activeTabId: remaining[remaining.length - 1] ?? null };
  });
}

function setActive(id: string | null, syncDock = true): void {
  store.setState((s) => {
    const panels: Record<string, WorkspacePanelState> = {};
    for (const [pid, p] of Object.entries(s.panels)) panels[pid] = { ...p, isActive: pid === id };
    // Only a TABBED panel moves the dock's front tab. Focusing a floating
    // window must leave it alone, or the tab behind that window is told it is
    // hidden and stops painting (see WorkspaceState.activeTabId).
    const nextTab = id && s.panels[id]?.mode === 'tabbed' ? id : s.activeTabId;
    return { panels, activeId: id, activeTabId: nextTab };
  });
  if (syncDock && id) {
    const p = panel(id);
    if (p?.mode === 'tabbed') getDockApi()?.getPanel(id)?.api.setActive();
  }
}

// ── persistence ─────────────────────────────────────────────────────────
function schedulePersist(): void {
  if (!ready) return;
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => workspace.saveLayout(), 250);
}

/** Bypass the debounce and persist immediately — the backstop for the gap
 * between a change and the 250ms timer firing: if the page is unloaded
 * (reload, close, navigate away) while a save is still pending, the very
 * last panel-focus/geometry change would otherwise never reach localStorage
 * and reopening would silently lose it. Mirrors `MarkdownEditor`'s own
 * `pagehide` autosave flush. */
function flushPersist(): void {
  if (!ready || !saveTimer) return;
  clearTimeout(saveTimer);
  saveTimer = null;
  workspace.saveLayout();
}

let pagehideAttached = false;
/** Attach the pagehide flush exactly once per session (bind() can re-run —
 * React StrictMode/HMR — and must never accumulate duplicate listeners). */
function ensurePagehideFlush(): void {
  if (pagehideAttached || typeof window === 'undefined') return;
  pagehideAttached = true;
  window.addEventListener('pagehide', flushPersist);
}

// ── controller ─────────────────────────────────────────────────────────
export const workspace = {
  /** Wire Dockview events once the layout is ready, then load persisted/default. */
  bind(api: DockviewApi): void {
    // Re-initialize cleanly for this Dockview instance (React StrictMode/HMR can
    // mount twice); auto-persist means no arrangement is lost across a remount.
    ready = false;
    ensurePagehideFlush();
    store.setState({
      panels: {},
      tabOrder: [],
      activeId: null,
      activeTabId: null,
      maximizedId: null,
      zTop: FLOATING_Z_BASE,
      snapPreview: null,
    });
    api.clear();

    // Reflect user tab clicks into our active state (no dock write-back loop).
    api.onDidActivePanelChange((e) => {
      const id = e.panel?.id;
      if (!id || panel(id)?.mode !== 'tabbed') return;
      // Always record the front tab, even when focus is elsewhere (a floating
      // window): Dockview switching tabs behind a floating panel still changes
      // which tab is on screen, and that is what paint-gating reads.
      if (get().activeTabId !== id) store.setState({ activeTabId: id });
      if (get().activeId !== id) setActive(id, false);
    });
    // Keep tab order in sync when Dockview reorders tabs (our in-strip reorder
    // drives `moveTo`, which fires this). Persist the new order too, so a reorder
    // survives reload — the raw store write alone never reaches localStorage.
    const syncTabOrder = () => {
      const group = api.groups[0];
      if (!group) return;
      const order = group.panels.map((p) => p.id).filter((pid) => panel(pid)?.mode === 'tabbed');
      if (order.length && order.join() !== get().tabOrder.join()) {
        store.setState({ tabOrder: order });
        schedulePersist();
      }
    };
    api.onDidMovePanel(syncTabOrder);

    ready = true;
    const persisted = loadLayout();
    if (persisted) this.hydrate(persisted);
    // A brand-new install (or a wiped/corrupt layout) has nothing to hydrate,
    // AND a layout where the user closed every tab hydrates to zero panels —
    // either way the dock would render totally blank. HOME is the landing
    // surface: it is the one that answers "what have I got?", which is the
    // question you actually open a notes app with. The atlas is a destination
    // you choose, not a place to be dropped.
    if (Object.keys(get().panels).length === 0) this.openHome();
  },

  genId(type: PanelType): string {
    let id: string;
    do id = `${type}-${++idCounter}`;
    while (get().panels[id]);
    return id;
  },

  /** Open (or focus) a document/note as a tab. THE entry point the explorer calls. */
  /**
   * Open a document panel. `background: true` creates the tab WITHOUT moving to
   * it — the ⌘/Ctrl-click contract: a browser opens the page in a tab you can
   * get to later and leaves you where you are.
   */
  openDoc(desc: DocDescriptor, opts?: { background?: boolean }): string {
    const pid = `doc:${desc.id}`;
    const exists = !!get().panels[pid];
    const restore = opts?.background ? get().activeId : null;
    if (exists) {
      if (!opts?.background) this.tabPanel(pid);
    } else {
      this.openPanel('doc', pid);
    }
    // Never clobber a good title with a blank incoming one: a deep-link /
    // Back-Forward re-open (use-router's applyRoute) passes "" as the title and
    // relies on the document query to restore the real one (DocPanel's
    // title-sync effect). Setting "" here would blank the tab until an
    // unrelated refetch. Only write a non-blank incoming title.
    if (desc.title.trim()) patch(pid, { title: desc.title });
    this.setData(pid, 'docId', desc.id);
    this.setData(pid, 'docKind', desc.docKind);
    this.setData(pid, 'nodeType', desc.nodeType ?? null);
    if (restore && get().panels[restore]) setActive(restore);
    return pid;
  },

  /** Open (or bring to front) the singleton home panel — the landing
   * destination that sits alongside Atlas and Orbit in the LeftBar ribbon. */
  openHome(): string {
    const id = 'home:main';
    if (get().panels[id]) this.tabPanel(id);
    else this.openPanel('home', id);
    patch(id, { title: 'Home' });
    return id;
  },

  /** Open (or bring to front) the singleton Folder View panel — the file
   * explorer. Singleton for the same reason Atlas is: it is a destination, not
   * a document, and two of them would just be two views of one vault. */
  openFiles(): string {
    const id = 'files:main';
    if (get().panels[id]) this.tabPanel(id);
    else this.openPanel('files', id);
    patch(id, { title: 'Files' });
    return id;
  },

  /** Open (or bring to front) the singleton atlas panel. THE entry point the
   * LeftBar ribbon's "Atlas" button and every `activate("graph")` legacy call
   * (via the `stores/tabs` shim) use — never duplicates. */
  openGraph(): string {
    const id = 'graph:main';
    if (get().panels[id]) this.tabPanel(id);
    else this.openPanel('graph', id);
    patch(id, { title: 'Atlas' });
    return id;
  },

  /** Open (or bring to front) the singleton orbits panel. */
  openOrbit(): string {
    const id = 'orbit:main';
    if (get().panels[id]) this.tabPanel(id);
    else this.openPanel('orbit', id);
    patch(id, { title: 'Orbits' });
    return id;
  },

  /** Open (or focus) a note/org-hub/AI-output's rich markdown body — the
   * counterpart to `openDoc` for markdown-backed nodes (vs. reader documents). */
  openMarkdown(nodeId: string, title: string, opts?: { background?: boolean }): string {
    const pid = `markdown:${nodeId}`;
    const restore = opts?.background ? get().activeId : null;
    if (get().panels[pid]) {
      if (!opts?.background) this.tabPanel(pid);
    } else {
      this.openPanel('markdown', pid);
    }
    // Same guard as openDoc: never blank a good title with an empty incoming
    // one — the node query restores it via MarkdownPanel's title-sync effect.
    if (title.trim()) patch(pid, { title });
    this.setData(pid, 'docId', nodeId);
    if (restore && get().panels[restore]) setActive(restore);
    return pid;
  },

  /** Open a NEW ask conversation panel (never a singleton — each call is a
   * fresh conversation, mirroring the old `stores/tabs`'s `openAsk`). Scope/
   * seed/conversation fields ride in `data`, read back out by `AskTab` via the
   * `stores/tabs` shim's `TabDesc` projection. */
  openAsk(opts?: {
    scopeDocIds?: string[];
    scopeNodeIds?: string[];
    seedQuery?: string;
    title?: string;
  }): string {
    const pid = this.openPanel('ask');
    patch(pid, { title: opts?.title ?? 'Ask' });
    if (opts?.scopeDocIds) this.setData(pid, 'scopeDocIds', opts.scopeDocIds);
    if (opts?.scopeNodeIds) this.setData(pid, 'scopeNodeIds', opts.scopeNodeIds);
    if (opts?.seedQuery) this.setData(pid, 'seedQuery', opts.seedQuery);
    return pid;
  },

  /** Reactive tab-title update (a doc/node rename, or its display title
   * changing) — a no-op if the panel is already showing that title, so a
   * query re-render with unchanged data never schedules a spurious persist. */
  setTitle(id: string, title: string): void {
    const p = panel(id);
    if (!p || !title || p.title === title) return;
    patch(id, { title });
    schedulePersist();
  },

  openPanel(type: PanelType, id?: string, mode: PanelMode = 'tabbed'): string {
    if (!(PANEL_TYPES as readonly string[]).includes(type)) {
      throw new Error(`unknown panel type: "${type}"`);
    }
    const pid = id ?? this.genId(type);
    if (get().panels[pid]) throw new Error(`panel "${pid}" already exists`);
    const base: WorkspacePanelState = {
      id: pid,
      type,
      title: 'Document',
      mode: 'tabbed',
      isActive: true,
    };
    store.setState((s) => ({ panels: { ...s.panels, [pid]: base }, tabOrder: [...s.tabOrder, pid] }));
    addToDock(pid);
    setActive(pid);
    if (mode !== 'tabbed') {
      if (mode === 'floating') this.floatPanel(pid);
      else if (isSnapped(mode)) this.snapPanel(pid, mode.replace('snapped-', '') as SnapRegion);
      else if (mode === 'maximized') this.maximizePanel(pid);
    }
    schedulePersist();
    return pid;
  },

  closePanel(id: string): void {
    requirePanel(id);
    removeFromDock(id);
    store.setState((s) => {
      const panels = { ...s.panels };
      delete panels[id];
      const tabOrder = s.tabOrder.filter((x) => x !== id);
      const remaining = Object.keys(panels);
      const activeId = s.activeId === id ? (tabOrder[tabOrder.length - 1] ?? remaining[0] ?? null) : s.activeId;
      // The closed panel may have been the dock's front tab; fall back to the
      // last remaining tab so the dock never points at something gone.
      const activeTabId =
        s.activeTabId === id ? (tabOrder[tabOrder.length - 1] ?? null) : s.activeTabId;
      const maximizedId = s.maximizedId === id ? null : s.maximizedId;
      return { panels, tabOrder, activeId, activeTabId, maximizedId };
    });
    if (get().activeId) setActive(get().activeId);
    // Closing the LAST panel used to leave a blank dock with no way back except
    // the rail. Landing on Home instead means "exiting the thing you were in"
    // always returns you somewhere, which is what a close button implies.
    if (Object.keys(get().panels).length === 0) this.openHome();
    schedulePersist();
  },

  /**
   * Close every panel and land on Home.
   *
   * Deliberately one operation rather than a loop over `closePanel`: closing
   * the last panel re-opens Home, so a loop would either re-open it partway
   * through and then close it again, or leave the strip empty depending on
   * ordering. Clearing the workspace once and then opening Home is the state
   * the user asked for, reached directly.
   */
  closeAll(): void {
    for (const id of Object.keys(get().panels)) {
      removeFromDock(id);
      releaseTab(id);
    }
    store.setState({
      panels: {},
      tabOrder: [],
      activeId: null,
      activeTabId: null,
      maximizedId: null,
    });
    this.openHome();
    schedulePersist();
  },

  focusPanel(id: string): void {
    const p = requirePanel(id);
    if (isFloatingLike(p.mode)) patch(id, { zIndex: nextZ() });
    setActive(id);
    schedulePersist();
  },

  floatPanel(id: string): void {
    const p = requirePanel(id);
    removeFromDock(id);
    releaseTab(id);
    const geom =
      isFloatingLike(p.mode) && p.x != null && p.y != null && p.width != null && p.height != null
        ? clamp(p.x, p.y, p.width, p.height)
        : defaultFloatGeom(p.type);
    store.setState((s) => ({
      tabOrder: s.tabOrder.filter((x) => x !== id),
      maximizedId: s.maximizedId === id ? null : s.maximizedId,
    }));
    patch(id, { mode: 'floating', ...geom, zIndex: nextZ(), previousState: null });
    setActive(id, false);
    schedulePersist();
  },

  tabPanel(id: string): void {
    const p = requirePanel(id);
    if (p.mode === 'tabbed') {
      setActive(id);
      schedulePersist();
      return;
    }
    store.setState((s) => ({ maximizedId: s.maximizedId === id ? null : s.maximizedId }));
    patch(id, {
      mode: 'tabbed',
      x: undefined,
      y: undefined,
      width: undefined,
      height: undefined,
      zIndex: undefined,
      previousState: null,
    });
    store.setState((s) => (s.tabOrder.includes(id) ? s : { tabOrder: [...s.tabOrder, id] }));
    addToDock(id);
    setActive(id);
    schedulePersist();
  },

  /** Set/clear the panel currently being dragged (reflow ignores it). */
  setDraggingId(id: string | null): void {
    draggingId = id;
  },

  /** The left-to-right order of tabbed panel ids as Dockview renders them (its
   * live group order; falls back to the store's tabOrder before Dockview binds).
   * The drag layer reads this to know a tab's current slot. */
  tabbedOrder(): string[] {
    const group = getDockApi()?.groups[0];
    const ids = group
      ? group.panels.map((p) => p.id)
      : get().tabOrder;
    return ids.filter((id) => panel(id)?.mode === 'tabbed');
  },

  /**
   * Move a tabbed panel to `index` within the tab strip — the in-strip reorder
   * (Obsidian-style). Drives Dockview's OWN panel order via `moveTo` (which
   * removes then re-inserts at `index` among the remaining tabs, so `index` is a
   * post-removal insertion index — see workspace/tab-reorder.ts) AND mirrors the
   * result into `tabOrder`, keeping the two authorities in lockstep. The moved
   * panel stays active (so its editor keeps focus + caret across the move — the
   * "still writable after reordering" requirement); persisted, debounced. No-op
   * if the panel is already at that slot or isn't tabbed.
   */
  reorderTab(id: string, index: number): void {
    const api = getDockApi();
    const group = api?.groups[0];
    const dp = api?.getPanel(id);
    if (!api || !group || !dp || panel(id)?.mode !== 'tabbed') return;
    const cur = group.panels.findIndex((p) => p.id === id);
    if (cur < 0) return;
    const clamped = Math.max(0, Math.min(index, group.panels.length - 1));
    if (cur === clamped) return;
    dp.api.moveTo({ group, position: 'center', index: clamped });
    const order = group.panels.map((p) => p.id).filter((pid) => panel(pid)?.mode === 'tabbed');
    store.setState({ tabOrder: order });
    schedulePersist();
  },

  snapPanel(id: string, region: SnapRegion, area: Rect = get().workspaceRect): void {
    if (!SNAP_MODE[region]) throw new Error(`unsupported snap location: "${region}"`);
    const p = requirePanel(id);
    removeFromDock(id);
    releaseTab(id);
    const rect = snapRect(region, area);
    const previousState = p.previousState ?? snapshot(p);
    store.setState((s) => ({
      tabOrder: s.tabOrder.filter((x) => x !== id),
      maximizedId: s.maximizedId === id ? null : s.maximizedId,
    }));
    patch(id, {
      mode: SNAP_MODE[region],
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
      zIndex: nextZ(),
      previousState,
    });
    setActive(id, false);
    schedulePersist();
  },

  maximizePanel(id: string): void {
    const p = requirePanel(id);
    if (p.mode === 'maximized') return;
    const previousState = p.previousState ?? snapshot(p);
    if (p.mode === 'tabbed') removeFromDock(id);
    releaseTab(id);
    store.setState((s) => ({ tabOrder: s.tabOrder.filter((x) => x !== id), maximizedId: id }));
    patch(id, { mode: 'maximized', zIndex: nextZ(), previousState });
    setActive(id, false);
    schedulePersist();
  },

  restorePanel(id: string): void {
    const p = requirePanel(id);
    store.setState((s) => ({ maximizedId: s.maximizedId === id ? null : s.maximizedId }));
    const prev = p.previousState;
    if (!prev) {
      patch(id, { mode: 'floating', ...defaultFloatGeom(p.type), zIndex: nextZ(), previousState: null });
      setActive(id, false);
      schedulePersist();
      return;
    }
    patch(id, { ...prev, isActive: true, zIndex: nextZ(), previousState: null });
    if (prev.mode === 'tabbed') {
      store.setState((s) => (s.tabOrder.includes(id) ? s : { tabOrder: [...s.tabOrder, id] }));
      addToDock(id);
      setActive(id);
    } else {
      setActive(id, false);
    }
    schedulePersist();
  },

  /** Live geometry update during a drag/resize gesture (no persistence churn). */
  setGeometry(id: string, mode: PanelMode, rect: { x: number; y: number; width: number; height: number }): void {
    patch(id, { mode, ...rect });
  },

  /** Finalize a drag/resize into a persisted floating position. */
  commitGeometry(id: string, rect: { x: number; y: number; width: number; height: number }): void {
    const p = requirePanel(id);
    const geom = clamp(rect.x, rect.y, rect.width, rect.height);
    patch(id, { mode: 'floating', ...geom, zIndex: p.zIndex ?? nextZ(), previousState: null });
    schedulePersist();
  },

  /** Re-apply snap rectangles and clamp floating panels after a viewport resize. */
  reflow(): void {
    const ws = get().workspaceRect;
    if (ws.width === 0 || ws.height === 0) return;
    for (const p of Object.values(get().panels)) {
      if (p.id === draggingId) continue;
      if (isSnapped(p.mode)) {
        const region = p.mode.replace('snapped-', '') as SnapRegion;
        patch(p.id, snapRect(region, ws));
      } else if (p.mode === 'floating' && p.x != null && p.y != null) {
        patch(p.id, clamp(p.x, p.y, p.width ?? 320, p.height ?? 240));
      }
    }
  },

  /** Default floating size for a panel type (used when detaching a tab). */
  defaultSize(type: PanelType): { width: number; height: number } {
    return DEFAULT_SIZE[type];
  },

  /** Detach a panel into floating drag mode at a given rect (no persistence). */
  beginFloatDrag(id: string, rect: { x: number; y: number; width: number; height: number }): void {
    requirePanel(id);
    removeFromDock(id);
    releaseTab(id);
    store.setState((s) => ({
      tabOrder: s.tabOrder.filter((x) => x !== id),
      maximizedId: s.maximizedId === id ? null : s.maximizedId,
    }));
    patch(id, { mode: 'floating', ...rect, zIndex: nextZ() });
    setActive(id, false);
  },

  /** Restore a panel to a captured snapshot (used to cancel a drag). */
  applyState(id: string, state: WorkspacePanelState): void {
    requirePanel(id);
    removeFromDock(id);
    if (state.mode === 'tabbed') {
      patch(id, {
        mode: 'tabbed',
        x: undefined,
        y: undefined,
        width: undefined,
        height: undefined,
        zIndex: undefined,
        previousState: state.previousState ?? null,
      });
      store.setState((s) => ({
        tabOrder: s.tabOrder.includes(id) ? s.tabOrder : [...s.tabOrder, id],
        maximizedId: s.maximizedId === id ? null : s.maximizedId,
      }));
      addToDock(id);
      setActive(id);
    } else if (state.mode === 'maximized') {
      patch(id, { ...state, zIndex: nextZ() });
      store.setState({ maximizedId: id });
      setActive(id, false);
    } else {
      patch(id, { ...state, zIndex: nextZ() });
      store.setState((s) => ({
        tabOrder: s.tabOrder.filter((x) => x !== id),
        maximizedId: s.maximizedId === id ? null : s.maximizedId,
      }));
      setActive(id, false);
    }
    schedulePersist();
  },

  /** Persist a piece of a panel's lifted content (docId / kind / node_type). */
  setData(id: string, key: string, value: unknown): void {
    const p = requirePanel(id);
    patch(id, { data: { ...(p.data ?? {}), [key]: value } });
    schedulePersist();
  },

  listPanels(): WorkspacePanelState[] {
    return Object.values(get().panels);
  },

  saveLayout(): void {
    const s = get();
    const data: PersistedLayout = {
      version: 1,
      panels: Object.values(s.panels),
      tabOrder: s.tabOrder,
      activeId: s.activeId,
      maximizedId: s.maximizedId,
      zTop: s.zTop,
    };
    saveLayout(data);
  },

  hydrate(data: PersistedLayout): void {
    for (const id of Object.keys(get().panels)) removeFromDock(id);
    getDockApi()?.clear();
    const panels: Record<string, WorkspacePanelState> = {};
    for (const p of data.panels) panels[p.id] = p;
    const restoredOrder = data.tabOrder.filter((id) => panels[id]);
    store.setState({
      panels,
      tabOrder: restoredOrder,
      activeId: data.activeId,
      // The persisted layout records focus, not which tab was in front; a
      // restored floating panel must not leave the dock pointing at nothing.
      activeTabId:
        data.activeId && panels[data.activeId]?.mode === 'tabbed'
          ? data.activeId
          : (restoredOrder[restoredOrder.length - 1] ?? null),
      maximizedId: data.maximizedId,
      // Clamp a stale/foreign persisted value into range too — hydrate() can
      // load a layout saved before FLOATING_Z_CAP existed, or one carried
      // over from a much longer session.
      zTop: Math.min(data.zTop ?? FLOATING_Z_BASE, FLOATING_Z_CAP),
      snapPreview: null,
    });
    for (const id of data.tabOrder) if (panels[id]?.mode === 'tabbed') addToDock(id);
    if (data.activeId) setActive(data.activeId);
  },
};

export type WorkspaceController = typeof workspace;

// Re-export ref setters used by the surface's layout effect (kept here so the
// surface imports a single module for controller wiring).
export { setWorkspaceEl, setTabStripEl };
